/**
 * Browse Alliances Dialog
 * Shows all alliances with search, sort, expandable detail, pagination, and Join
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import { Colors, CSSColors, Fonts, FontSize, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';
const CARD_HEIGHT = 78;
const CARD_GAP = 6;
const CONTENT_TOP = 110;
const PAGE_SIZE = 5;

type SortMode = 'level' | 'members' | 'newest';

interface AllianceListItem {
  id: number;
  name: string;
  tag: string;
  leaderId: string;
  leaderName: string;
  description: string;
  memberCount: number;
  memberMax: number;
  createdAt: string;
  level: number;
}

// ── Alliance member cap formula ──
// Base: 10 slots on creation (free)
// Each owned station adds slots by tier:
//   Waypoint ($500):   +5
//   Depot ($2,000):   +10
//   Fortress ($10,000): +15
//   Capital ($50,000):  +25
// Hard cap: 100 members regardless of stations

const MOCK_ALLIANCES: AllianceListItem[] = [
  {
    id: 1, name: 'Nova Imperium', tag: 'NOV', leaderId: 'u1', leaderName: 'StarLord',
    description: 'Elite PvP alliance. Raid nights Wed & Sat. Top 10 last season. Looking for experienced pilots who can commit to weekly operations.',
    memberCount: 87, memberMax: 100, createdAt: '2025-11-15', level: 12,
  },
  {
    id: 2, name: 'Void Runners', tag: 'VDR', leaderId: 'u2', leaderName: 'DarkNebula',
    description: 'Casual & friendly. New players welcome! Active daily. We run mining ops and help newer commanders learn the ropes.',
    memberCount: 28, memberMax: 35, createdAt: '2025-12-03', level: 7,
  },
  {
    id: 3, name: 'Steel Phalanx', tag: 'SPX', leaderId: 'u3', leaderName: 'IronCommander',
    description: 'Competitive territorial control. EU timezone focus. Coordinated fleet operations every evening.',
    memberCount: 43, memberMax: 55, createdAt: '2025-10-22', level: 9,
  },
  {
    id: 4, name: 'Cosmic Traders', tag: 'CTR', leaderId: 'u4', leaderName: 'MerchantQueen',
    description: 'Trade-focused alliance. Auction house domination. Mineral routes mapped and shared with all members.',
    memberCount: 18, memberMax: 20, createdAt: '2026-01-10', level: 5,
  },
  {
    id: 5, name: 'Shadow Fleet', tag: 'SHD', leaderId: 'u5', leaderName: 'PhantomAce',
    description: 'Stealth ops & deep space raids. We hit hard and disappear. Small but deadly crew.',
    memberCount: 15, memberMax: 15, createdAt: '2026-01-28', level: 4,
  },
  {
    id: 6, name: 'Frontier Guard', tag: 'FTG', leaderId: 'u6', leaderName: 'Sentinel',
    description: 'Protecting new players in the outer rim. Patrol & escort missions. Honor-bound alliance with strict rules of engagement.',
    memberCount: 39, memberMax: 45, createdAt: '2025-11-30', level: 8,
  },
  {
    id: 7, name: 'Aether Collective', tag: 'AET', leaderId: 'u7', leaderName: 'NovaStar',
    description: 'Science & exploration focused. Weekly deep space expeditions to uncharted sectors.',
    memberCount: 8, memberMax: 10, createdAt: '2026-02-01', level: 3,
  },
  {
    id: 8, name: 'Iron Legion', tag: 'IRL', leaderId: 'u8', leaderName: 'Warlord',
    description: 'Full roster. Dominating the core sectors since day one. The oldest and largest alliance in the game.',
    memberCount: 100, memberMax: 100, createdAt: '2025-09-01', level: 15,
  },
  {
    id: 9, name: 'Crimson Wolves', tag: 'CRW', leaderId: 'u9', leaderName: 'Bloodfang',
    description: 'Aggressive expansion. No mercy, no retreat. Full ranks — we only accept the best.',
    memberCount: 25, memberMax: 25, createdAt: '2025-10-10', level: 11,
  },
  {
    id: 10, name: 'Starfall', tag: 'STF', leaderId: 'u10', leaderName: 'LunaEcho',
    description: 'Relaxed group for weekend warriors. Mining and trade runs. No pressure, just fun.',
    memberCount: 6, memberMax: 10, createdAt: '2026-02-05', level: 2,
  },
  {
    id: 11, name: 'Orbital Kings', tag: 'ORK', leaderId: 'u11', leaderName: 'KingPin',
    description: 'Station control specialists. Insurance & economy focused. We own the trade lanes.',
    memberCount: 38, memberMax: 40, createdAt: '2025-12-20', level: 6,
  },
  {
    id: 12, name: 'Deep Six', tag: 'D6X', leaderId: 'u12', leaderName: 'Abyssal',
    description: 'Deep space exploration crew. Wormhole runners. Currently at capacity but check back soon.',
    memberCount: 30, memberMax: 30, createdAt: '2025-11-05', level: 10,
  },
];

export class BrowseAlliancesDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private dh: number;

  private mmoState: MMOStateManager;
  private mmoNetwork: MMONetworkManager;

  // UI
  private contentContainer: Phaser.GameObjects.Container;
  private scrollY: number = 0;
  private maxScrollY: number = 0;
  private maskGraphics?: Phaser.GameObjects.Graphics;
  private searchOverlay?: HTMLDivElement;
  private searchInput?: HTMLInputElement;
  private sortSelect?: HTMLSelectElement;
  private paginationOverlay?: HTMLDivElement;

  // Data
  private allAlliances: AllianceListItem[] = [];
  private filteredAlliances: AllianceListItem[] = [];
  private currentPage: number = 0;
  private sortMode: SortMode = 'level';
  private tooltipEl?: HTMLDivElement;

  // Event handlers
  private onAllianceListHandler: (payload: any) => void;
  private onAllianceJoinedHandler: (payload: any) => void;

  constructor(scene: Phaser.Scene, mmoState: MMOStateManager, mmoNetwork: MMONetworkManager) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 720;
    const dh = mobile ? 540 : 620;
    const { width, height } = scene.cameras.main;
    super(scene, (width - dw) / 2, (height - dh) / 2);
    this.dw = dw;
    this.dh = dh;

    this.mmoState = mmoState;
    this.mmoNetwork = mmoNetwork;

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.browse_alliances'),
      onClose: () => this.close(),
    });

    this.createToolbar();

    this.contentContainer = scene.add.container(20, CONTENT_TOP);
    this.add(this.contentContainer);

    this.setupScrollMask();

    this.onAllianceListHandler = (payload: any) => this.onAllianceList(payload);
    this.onAllianceJoinedHandler = (payload: any) => this.onAllianceJoined(payload);
    EventBus.on(MMOEvents.ALLIANCE_LIST, this.onAllianceListHandler);
    EventBus.on(MMOEvents.ALLIANCE_JOINED, this.onAllianceJoinedHandler);

    this.mmoNetwork.sendAllianceList();

    this.allAlliances = MOCK_ALLIANCES;
    this.filteredAlliances = MOCK_ALLIANCES;
    this.renderAllianceList();

    this.setDepth(2000);
    scene.add.existing(this);
  }

  // ═══════════════════════════════════════════
  //  TOOLBAR: Search + Sort dropdown
  // ═══════════════════════════════════════════
  private createToolbar(): void {
    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const gameW = this.scene.cameras.main.width;
    const gameH = this.scene.cameras.main.height;
    const sx = canvasRect.width / gameW;
    const sy = canvasRect.height / gameH;

    const toolbarLeft = canvasRect.left + (this.x + 20) * sx;
    const toolbarTop = canvasRect.top + (this.y + 60) * sy;
    const toolbarW = (this.dw - 40) * sx;
    const toolbarH = 36 * sy;
    const sortW = 150 * sx;
    this.searchOverlay = document.createElement('div');
    this.searchOverlay.style.cssText = `
      position:fixed; left:${toolbarLeft}px; top:${toolbarTop}px;
      width:${toolbarW}px; height:${toolbarH}px;
      z-index:100001; pointer-events:auto;
      display:flex; gap:${10 * sx}px; align-items:center;
    `;

    const stop = (e: Event) => e.stopPropagation();
    ['mousedown', 'mouseup', 'click', 'touchstart', 'touchend', 'keydown', 'keyup', 'wheel']
      .forEach(evt => this.searchOverlay!.addEventListener(evt, stop));
    this.searchOverlay.addEventListener('focusin', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = false;
      EventBus.emit('chat-input-focused');
    });
    this.searchOverlay.addEventListener('focusout', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
      EventBus.emit('chat-input-unfocused');
    });

    const inputCss = `
      background:#0a1525; color:#e0e8ff; border:1.5px solid #1e3a5e; border-radius:6px;
      font-family:${Fonts.body}; outline:none; caret-color:#00ffff;
      transition:border-color .15s, box-shadow .15s;
    `;
    const addFB = (el: HTMLElement) => {
      el.addEventListener('focus', () => { el.style.borderColor = '#FDB15B'; el.style.boxShadow = '0 0 8px rgba(253,177,91,0.35)'; });
      el.addEventListener('blur', () => { el.style.borderColor = '#1e3a5e'; el.style.boxShadow = 'none'; });
    };

    // Search container (with icon)
    const searchWrap = document.createElement('div');
    searchWrap.style.cssText = `position:relative; flex:1; height:100%;`;

    const icon = document.createElement('span');
    icon.textContent = '\u{1F50D}';
    icon.style.cssText = `position:absolute; left:${10 * sx}px; top:50%; transform:translateY(-50%); font-size:${13 * sy}px; pointer-events:none; opacity:0.4;`;
    searchWrap.appendChild(icon);

    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Search by name or tag...';
    this.searchInput.style.cssText = `${inputCss} width:100%; height:100%; box-sizing:border-box; font-size:${13 * sy}px; padding:0 ${12 * sx}px 0 ${34 * sx}px;`;
    addFB(this.searchInput);
    this.searchInput.addEventListener('input', () => this.applyFilter());
    searchWrap.appendChild(this.searchInput);
    this.searchOverlay.appendChild(searchWrap);

    // Sort dropdown
    this.sortSelect = document.createElement('select');
    this.sortSelect.style.cssText = `${inputCss} width:${sortW}px; height:100%; font-size:${12 * sy}px; padding:0 ${8 * sx}px; cursor:pointer; color:#88ddff; appearance:auto;`;
    addFB(this.sortSelect);
    const options: { value: SortMode; label: string }[] = [
      { value: 'level', label: 'Highest Level' },
      { value: 'members', label: 'Most Members' },
      { value: 'newest', label: 'Newest First' },
    ];
    options.forEach(o => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      opt.style.cssText = 'background:#0a1525; color:#e0e8ff;';
      this.sortSelect!.appendChild(opt);
    });
    this.sortSelect.value = this.sortMode;
    this.sortSelect.addEventListener('change', () => {
      this.sortMode = this.sortSelect!.value as SortMode;
      this.currentPage = 0;
      this.renderAllianceList();
    });
    this.searchOverlay.appendChild(this.sortSelect);

    document.body.appendChild(this.searchOverlay);
  }

  // ═══════════════════════════════════════════
  //  FILTER + SORT
  // ═══════════════════════════════════════════
  private applyFilter(): void {
    const query = (this.searchInput?.value || '').trim().toLowerCase();
    if (!query) {
      this.filteredAlliances = this.allAlliances;
    } else {
      this.filteredAlliances = this.allAlliances.filter(a =>
        a.name.toLowerCase().includes(query) || a.tag.toLowerCase().includes(query)
      );
    }
    this.scrollY = 0;
    this.currentPage = 0;
    this.renderAllianceList();
  }

  private sortAlliances(list: AllianceListItem[]): AllianceListItem[] {
    return [...list].sort((a, b) => {
      // Always push full alliances to bottom
      const aFull = a.memberCount >= a.memberMax ? 1 : 0;
      const bFull = b.memberCount >= b.memberMax ? 1 : 0;
      if (aFull !== bFull) return aFull - bFull;

      switch (this.sortMode) {
        case 'members':
          return b.memberCount - a.memberCount || b.level - a.level;
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'level':
        default:
          return b.level - a.level || b.memberCount - a.memberCount;
      }
    });
  }

  // ═══════════════════════════════════════════
  //  SCROLL
  // ═══════════════════════════════════════════
  private setupScrollMask(): void {
    const maskX = this.x + 20;
    const maskY = this.y + CONTENT_TOP;
    const maskW = this.dw - 40;
    const maskH = this.dh - CONTENT_TOP - 50;

    this.maskGraphics = this.scene.add.graphics();
    this.maskGraphics.fillStyle(0xffffff);
    this.maskGraphics.fillRect(maskX, maskY, maskW, maskH);
    this.contentContainer.setMask(this.maskGraphics.createGeometryMask());

    this.scene.input.on('wheel', (_pointer: any, _gameObjects: any, _dx: number, dy: number) => {
      if (!this.visible) return;
      this.scrollY = Phaser.Math.Clamp(this.scrollY - dy * 0.5, -this.maxScrollY, 0);
      this.contentContainer.y = CONTENT_TOP + this.scrollY;
    });
  }

  // ═══════════════════════════════════════════
  //  SERVER DATA
  // ═══════════════════════════════════════════
  private onAllianceList(payload: any): void {
    if (!payload || !payload.alliances) return;
    const alliances: AllianceListItem[] = payload.alliances.map((a: any) => ({
      ...a,
      memberMax: a.memberMax ?? 50,
      level: a.level ?? Math.min(15, Math.max(1, Math.ceil((a.memberCount || 1) / 3))),
    }));
    if (alliances.length === 0) return;
    this.allAlliances = alliances;
    this.applyFilter();
  }

  // ═══════════════════════════════════════════
  //  RENDER LIST
  // ═══════════════════════════════════════════
  private get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredAlliances.length / PAGE_SIZE));
  }

  private renderAllianceList(): void {
    this.contentContainer.removeAll(true);
    this.scrollY = 0;
    this.contentContainer.y = CONTENT_TOP;
    const playerInAlliance = this.mmoState.getAlliance() !== null;
    const sorted = this.sortAlliances(this.filteredAlliances);

    // Empty state
    if (sorted.length === 0) {
      this.renderEmptyState();
      this.updatePagination();
      return;
    }

    if (this.currentPage >= this.totalPages) this.currentPage = this.totalPages - 1;

    const start = this.currentPage * PAGE_SIZE;
    const pageItems = sorted.slice(start, start + PAGE_SIZE);

    // Column headers
    const cardW = this.dw - 40;
    const headers = [
      { text: 'ALLIANCE', x: 70 },
      { text: 'MEMBERS', x: cardW - 160 },
    ];
    headers.forEach(h => {
      this.contentContainer.add(this.scene.add.text(h.x, 0, h.text, {
        fontSize: '10px', color: CSSColors.textDim, fontFamily: Fonts.heading,
      }));
    });

    let yOffset = 20;
    pageItems.forEach((alliance, idx) => {
      const card = this.createAllianceCard(0, yOffset, alliance, playerInAlliance, idx);
      this.contentContainer.add(card);
      yOffset += CARD_HEIGHT + CARD_GAP;
    });

    const visibleH = this.dh - CONTENT_TOP - 50;
    this.maxScrollY = Math.max(0, yOffset - visibleH);
    this.updatePagination();
  }

  // ═══════════════════════════════════════════
  //  EMPTY STATE with CREATE CTA
  // ═══════════════════════════════════════════
  private renderEmptyState(): void {
    const cx = (this.dw - 40) / 2;
    const query = (this.searchInput?.value || '').trim();

    if (query) {
      // Search with no results
      const msg = this.scene.add.text(cx, 80, `No alliances match "${query}"`, {
        fontSize: FontSize.md, color: CSSColors.textDim, fontFamily: Fonts.body, align: 'center',
      });
      msg.setOrigin(0.5, 0);
      this.contentContainer.add(msg);
    } else {
      // No alliances exist — engaging CTA
      const icon = this.scene.add.text(cx, 60, '\u2694', { fontSize: '36px' });
      icon.setOrigin(0.5, 0);
      this.contentContainer.add(icon);

      const title = this.scene.add.text(cx, 110, 'No alliances yet', {
        fontSize: FontSize.xl, color: '#e0e8ff', fontFamily: Fonts.heading, align: 'center',
      });
      title.setOrigin(0.5, 0);
      this.contentContainer.add(title);

      const sub = this.scene.add.text(cx, 140, 'Be the first to forge an alliance\nand claim the frontier.', {
        fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body, align: 'center', lineSpacing: 4,
      });
      sub.setOrigin(0.5, 0);
      this.contentContainer.add(sub);

      // CREATE ALLIANCE button
      const btnW = 200, btnH = 40;
      const btnX = cx - btnW / 2, btnY = 190;
      const btnBg = this.scene.add.graphics();
      btnBg.fillStyle(0x003322, 0.8);
      btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
      btnBg.lineStyle(1.5, Colors.cyan, 0.5);
      btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      this.contentContainer.add(btnBg);

      const btnLabel = this.scene.add.text(cx, btnY + btnH / 2, 'CREATE ALLIANCE', {
        fontSize: FontSize.md, color: CSSColors.cyan, fontFamily: Fonts.heading,
      });
      btnLabel.setOrigin(0.5);
      this.contentContainer.add(btnLabel);

      const btnZone = this.scene.add.zone(cx, btnY + btnH / 2, btnW, btnH).setInteractive({ useHandCursor: true });
      btnZone.on('pointerover', () => {
        btnBg.clear();
        btnBg.fillStyle(0x005544, 0.9);
        btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
        btnBg.lineStyle(1.5, Colors.cyan, 0.8);
        btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      });
      btnZone.on('pointerout', () => {
        btnBg.clear();
        btnBg.fillStyle(0x003322, 0.8);
        btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
        btnBg.lineStyle(1.5, Colors.cyan, 0.5);
        btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      });
      btnZone.on('pointerdown', () => {
        this.close();
        EventBus.emit('mmo-open-alliance-dialog');
      });
      this.contentContainer.add(btnZone);
    }
  }

  // ═══════════════════════════════════════════
  //  PAGINATION
  // ═══════════════════════════════════════════
  private updatePagination(): void {
    if (this.paginationOverlay) { this.paginationOverlay.remove(); this.paginationOverlay = undefined; }

    const total = this.totalPages;
    if (total <= 1 && this.filteredAlliances.length <= PAGE_SIZE) return;

    const canvas = this.scene.game.canvas;
    const cr = canvas.getBoundingClientRect();
    const sx = cr.width / this.scene.cameras.main.width;
    const sy = cr.height / this.scene.cameras.main.height;

    const pLeft = cr.left + (this.x + 20) * sx;
    const pTop = cr.top + (this.y + this.dh - 48) * sy;
    const pW = (this.dw - 40) * sx;

    this.paginationOverlay = document.createElement('div');
    this.paginationOverlay.style.cssText = `
      position:fixed; left:${pLeft}px; top:${pTop}px;
      width:${pW}px; height:${32 * sy}px;
      z-index:100001; pointer-events:auto;
      display:flex; align-items:center; justify-content:center; gap:${12 * sx}px;
      font-family:${Fonts.heading};
    `;
    const stop = (e: Event) => e.stopPropagation();
    ['mousedown', 'mouseup', 'click', 'touchstart', 'touchend', 'keydown', 'keyup', 'wheel']
      .forEach(evt => this.paginationOverlay!.addEventListener(evt, stop));

    const hasPrev = this.currentPage > 0;
    const hasNext = this.currentPage < total - 1;

    const mkBtn = (label: string, enabled: boolean, onClick: () => void) => {
      const btn = document.createElement('button');
      btn.textContent = label;
      btn.disabled = !enabled;
      btn.style.cssText = `
        background:none; border:1px solid ${enabled ? '#1e3a5e' : '#0d1a2a'}; border-radius:4px;
        color:${enabled ? CSSColors.cyan : '#223344'}; font-family:${Fonts.heading};
        font-size:${11 * sy}px; padding:${4 * sy}px ${12 * sx}px;
        cursor:${enabled ? 'pointer' : 'default'}; transition:all .15s;
      `;
      if (enabled) {
        btn.addEventListener('mouseenter', () => { btn.style.borderColor = '#FDB15B'; btn.style.color = '#FDB15B'; });
        btn.addEventListener('mouseleave', () => { btn.style.borderColor = '#1e3a5e'; btn.style.color = CSSColors.cyan; });
        btn.addEventListener('click', onClick);
      }
      return btn;
    };

    this.paginationOverlay.appendChild(mkBtn('\u25C0  PREV', hasPrev, () => { this.currentPage--; this.renderAllianceList(); }));

    const pageInfo = document.createElement('span');
    pageInfo.textContent = `${this.currentPage + 1} / ${total}`;
    pageInfo.style.cssText = `font-size:${12 * sy}px; color:${CSSColors.textMuted}; min-width:${40 * sx}px; text-align:center;`;
    this.paginationOverlay.appendChild(pageInfo);

    this.paginationOverlay.appendChild(mkBtn('NEXT  \u25B6', hasNext, () => { this.currentPage++; this.renderAllianceList(); }));

    const countInfo = document.createElement('span');
    countInfo.textContent = `${this.filteredAlliances.length} alliance${this.filteredAlliances.length !== 1 ? 's' : ''}`;
    countInfo.style.cssText = `position:absolute; right:0; font-size:${10 * sy}px; color:${CSSColors.textDim}; font-family:${Fonts.body};`;
    this.paginationOverlay.appendChild(countInfo);

    document.body.appendChild(this.paginationOverlay);
  }

  // ═══════════════════════════════════════════
  //  ALLIANCE DETAIL VIEW
  // ═══════════════════════════════════════════
  private showAllianceDetail(alliance: AllianceListItem): void {
    this.contentContainer.removeAll(true);
    this.scrollY = 0;
    this.contentContainer.y = CONTENT_TOP;

    const cx = (this.dw - 40) / 2;
    const playerInAlliance = this.mmoState.getAlliance() !== null;

    // Back button
    const backText = this.scene.add.text(10, 0, '\u2190 Back', {
      fontSize: FontSize.sm, color: CSSColors.cyan, fontFamily: Fonts.body,
    }).setInteractive({ useHandCursor: true });
    backText.on('pointerdown', () => this.renderAllianceList());
    backText.on('pointerover', () => backText.setColor('#88ffff'));
    backText.on('pointerout', () => backText.setColor(CSSColors.cyan));
    this.contentContainer.add(backText);

    // Emblem
    if (this.scene.textures.exists('alliance-badge')) {
      const emblem = this.scene.add.image(cx, 50, 'alliance-badge');
      emblem.setDisplaySize(60, 60);
      this.contentContainer.add(emblem);
    }

    // Name + Tag
    const nameText = this.scene.add.text(cx, 90, `${alliance.name} [${alliance.tag}]`, {
      fontSize: FontSize.xl, color: '#e0e8ff', fontFamily: Fonts.heading, align: 'center',
    });
    nameText.setOrigin(0.5, 0);
    this.contentContainer.add(nameText);

    // Description
    const descText = this.scene.add.text(cx, 120, alliance.description || 'No description', {
      fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body, align: 'center',
      wordWrap: { width: this.dw - 100 },
    });
    descText.setOrigin(0.5, 0);
    this.contentContainer.add(descText);

    // Stats
    const statsY = descText.y + descText.height + 20;
    const stats = [
      { label: 'Members', value: `${alliance.memberCount} / ${alliance.memberMax}` },
      { label: 'Level', value: `${alliance.level}` },
      { label: 'Leader', value: alliance.leaderName || 'Unknown' },
    ];
    stats.forEach((s, i) => {
      const sy = statsY + i * 24;
      this.contentContainer.add(this.scene.add.text(cx - 80, sy, s.label, {
        fontSize: FontSize.sm, color: CSSColors.textDim, fontFamily: Fonts.body,
      }));
      this.contentContainer.add(this.scene.add.text(cx + 40, sy, s.value, {
        fontSize: FontSize.sm, color: CSSColors.textLight, fontFamily: Fonts.heading,
      }));
    });

    // Join button (if not already in an alliance and not full)
    if (!playerInAlliance && alliance.memberCount < alliance.memberMax) {
      const btnW = 160, btnH = 40;
      const btnX = cx - btnW / 2, btnY = statsY + stats.length * 24 + 20;
      const btnBg = this.scene.add.graphics();
      btnBg.fillStyle(0x003322, 0.8);
      btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
      btnBg.lineStyle(1.5, Colors.cyan, 0.5);
      btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      this.contentContainer.add(btnBg);

      const btnLabel = this.scene.add.text(cx, btnY + btnH / 2, 'JOIN ALLIANCE', {
        fontSize: FontSize.md, color: CSSColors.cyan, fontFamily: Fonts.heading,
      });
      btnLabel.setOrigin(0.5);
      this.contentContainer.add(btnLabel);

      const btnZone = this.scene.add.zone(cx, btnY + btnH / 2, btnW, btnH).setInteractive({ useHandCursor: true });
      btnZone.on('pointerover', () => {
        btnBg.clear();
        btnBg.fillStyle(0x005544, 0.9);
        btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
        btnBg.lineStyle(1.5, Colors.cyan, 0.7);
        btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      });
      btnZone.on('pointerout', () => {
        btnBg.clear();
        btnBg.fillStyle(0x003322, 0.8);
        btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
        btnBg.lineStyle(1.5, Colors.cyan, 0.5);
        btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      });
      btnZone.on('pointerdown', () => {
        EventBus.emit('send_message', {
          type: 'mmo_alliance_join',
          payload: { allianceId: alliance.id },
        });
        this.destroy();
      });
      this.contentContainer.add(btnZone);
    }
  }

  // ═══════════════════════════════════════════
  //  ALLIANCE CARD (collapsed + expanded)
  // ═══════════════════════════════════════════
  private createAllianceCard(
    x: number, y: number, alliance: AllianceListItem,
    playerInAlliance: boolean, idx: number,
  ): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);
    const cardW = this.dw - 40;

    // Background
    const bgColor = idx % 2 === 0 ? Colors.bgCard : Colors.bgPanel;
    const bg = this.scene.add.graphics();
    bg.fillStyle(bgColor, 0.6);
    bg.fillRoundedRect(0, 0, cardW, CARD_HEIGHT, 6);
    bg.lineStyle(1, Colors.borderDim, 0.2);
    bg.strokeRoundedRect(0, 0, cardW, CARD_HEIGHT, 6);
    card.add(bg);

    // Hover highlight zone (whole card minus join button area) — clickable to view details
    const hoverZone = this.scene.add.zone(cardW / 2 - 40, CARD_HEIGHT / 2, cardW - 80, CARD_HEIGHT).setInteractive({ useHandCursor: true });
    hoverZone.on('pointerdown', () => {
      this.showAllianceDetail(alliance);
    });
    hoverZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(Colors.bgElevated, 0.7);
      bg.fillRoundedRect(0, 0, cardW, CARD_HEIGHT, 6);
      bg.lineStyle(1, Colors.cyan, 0.15);
      bg.strokeRoundedRect(0, 0, cardW, CARD_HEIGHT, 6);
    });
    hoverZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(bgColor, 0.6);
      bg.fillRoundedRect(0, 0, cardW, CARD_HEIGHT, 6);
      bg.lineStyle(1, Colors.borderDim, 0.2);
      bg.strokeRoundedRect(0, 0, cardW, CARD_HEIGHT, 6);
    });
    card.add(hoverZone);

    // ── Row content ──
    const ecx = 34, ecy = CARD_HEIGHT / 2;
    if (this.scene.textures.exists('alliance-badge')) {
      const emblem = this.scene.add.image(ecx, ecy, 'alliance-badge');
      emblem.setDisplaySize(44, 44); emblem.setOrigin(0.5, 0.5);
      card.add(emblem);
    }

    // Level badge
    const lvlBg = this.scene.add.graphics();
    lvlBg.fillStyle(Colors.bgDeep, 0.95); lvlBg.fillCircle(ecx + 15, ecy + 15, 9);
    lvlBg.lineStyle(1, Colors.gold, 0.7); lvlBg.strokeCircle(ecx + 15, ecy + 15, 9);
    card.add(lvlBg);
    const lvlText = this.scene.add.text(ecx + 15, ecy + 15, `${alliance.level}`, { fontSize: '9px', color: CSSColors.gold, fontFamily: Fonts.heading });
    lvlText.setOrigin(0.5); card.add(lvlText);

    // Name + Tag
    const nameX = 68;
    const nameRow = this.scene.add.text(nameX, 12, alliance.name, { fontSize: FontSize.md, color: '#e0e8ff', fontFamily: Fonts.heading });
    card.add(nameRow);
    card.add(this.scene.add.text(nameX + nameRow.width + 6, 13, `[${alliance.tag}]`, { fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading }));

    // Description (truncated with tooltip on hover)
    const descMaxW = cardW - 250;
    const descTrunc = alliance.description.length > 60 ? alliance.description.substring(0, 57) + '...' : alliance.description;
    const descText = this.scene.add.text(nameX, 36, descTrunc, { fontSize: '11px', color: CSSColors.textMuted, fontFamily: Fonts.body, wordWrap: { width: descMaxW } });
    card.add(descText);

    // Description tooltip on hover (only if description is long enough to be truncated)
    if (alliance.description.length > 60) {
      const descZone = this.scene.add.zone(nameX + descMaxW / 2, 40, descMaxW, 20).setInteractive({ useHandCursor: false });
      descZone.on('pointerover', (pointer: Phaser.Input.Pointer) => {
        this.showTooltip(alliance.description, pointer.x, pointer.y);
      });
      descZone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
        this.moveTooltip(pointer.x, pointer.y);
      });
      descZone.on('pointerout', () => {
        this.hideTooltip();
      });
      card.add(descZone);
    }

    // Members column
    const memX = cardW - 160;
    const memFill = alliance.memberCount / alliance.memberMax;
    const memColor = memFill >= 1 ? CSSColors.red : memFill > 0.7 ? CSSColors.orange : CSSColors.textLight;
    card.add(this.scene.add.text(memX, 22, `${alliance.memberCount}`, { fontSize: FontSize.lg, color: memColor, fontFamily: Fonts.heading }));
    card.add(this.scene.add.text(memX, 44, `/ ${alliance.memberMax}`, { fontSize: '10px', color: CSSColors.textDim, fontFamily: Fonts.body }));

    // Join / Full button
    const isFull = alliance.memberCount >= alliance.memberMax;
    const btnW = 64, btnH = 32;
    const btnX = cardW - btnW - 12, btnY = (CARD_HEIGHT - btnH) / 2;

    if (isFull) {
      const fl = this.scene.add.text(btnX + btnW / 2, btnY + btnH / 2, 'FULL', { fontSize: FontSize.sm, fontFamily: Fonts.heading, color: CSSColors.red });
      fl.setOrigin(0.5); fl.setAlpha(0.6); card.add(fl);
    } else if (!playerInAlliance) {
      this.addJoinButton(card, btnX, btnY, btnW, btnH, alliance);
    } else {
      const ml = this.scene.add.text(btnX + btnW / 2, btnY + btnH / 2, 'JOIN', { fontSize: FontSize.sm, fontFamily: Fonts.heading, color: CSSColors.textDim });
      ml.setOrigin(0.5); ml.setAlpha(0.3); card.add(ml);
    }

    return card;
  }

  // ── Tooltip helpers ──
  private showTooltip(text: string, screenX: number, screenY: number): void {
    this.hideTooltip();

    const tip = document.createElement('div');
    tip.style.cssText = `
      position:fixed; z-index:100001; pointer-events:none;
      max-width:320px; padding:10px 14px;
      background:rgba(8,18,35,0.96); border:1px solid rgba(0,255,255,0.25);
      border-radius:6px; box-shadow:0 4px 20px rgba(0,0,0,0.6);
      color:#c8d4f0; font-family:${Fonts.body}; font-size:12px;
      line-height:1.5; white-space:pre-wrap;
    `;
    tip.textContent = text;

    document.body.appendChild(tip);
    this.tooltipEl = tip;
    this.moveTooltip(screenX, screenY);
  }

  private moveTooltip(screenX: number, screenY: number): void {
    if (!this.tooltipEl) return;
    // Position above and to the right of cursor, clamped to viewport
    const pad = 12;
    let tx = screenX + pad;
    let ty = screenY - pad - 40;
    const rect = this.tooltipEl.getBoundingClientRect();
    if (tx + rect.width > window.innerWidth - 8) tx = screenX - rect.width - pad;
    if (ty < 8) ty = screenY + pad;
    this.tooltipEl.style.left = `${tx}px`;
    this.tooltipEl.style.top = `${ty}px`;
  }

  private hideTooltip(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = undefined;
    }
  }

  // ── Reusable small JOIN button ──
  private addJoinButton(card: Phaser.GameObjects.Container, btnX: number, btnY: number, btnW: number, btnH: number, alliance: AllianceListItem): void {
    const joinBg = this.scene.add.graphics();
    joinBg.fillStyle(0x003322, 0.8);
    joinBg.fillRoundedRect(btnX, btnY, btnW, btnH, 4);
    joinBg.lineStyle(1.5, Colors.green, 0.6);
    joinBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 4);
    card.add(joinBg);

    const joinLabel = this.scene.add.text(btnX + btnW / 2, btnY + btnH / 2, 'JOIN', {
      fontSize: FontSize.sm, fontFamily: Fonts.heading, color: CSSColors.green,
    });
    joinLabel.setOrigin(0.5); card.add(joinLabel);

    const btnZone = this.scene.add.zone(btnX + btnW / 2, btnY + btnH / 2, btnW, btnH).setInteractive({ useHandCursor: true });
    btnZone.on('pointerover', () => {
      joinBg.clear();
      joinBg.fillStyle(0x005533, 0.9); joinBg.fillRoundedRect(btnX, btnY, btnW, btnH, 4);
      joinBg.lineStyle(1.5, Colors.green, 0.9); joinBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 4);
    });
    btnZone.on('pointerout', () => {
      joinBg.clear();
      joinBg.fillStyle(0x003322, 0.8); joinBg.fillRoundedRect(btnX, btnY, btnW, btnH, 4);
      joinBg.lineStyle(1.5, Colors.green, 0.6); joinBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 4);
    });
    btnZone.on('pointerdown', () => {
      this.mmoNetwork.sendAllianceJoin(alliance.id);
      joinLabel.setText('...');
      btnZone.disableInteractive();
    });
    card.add(btnZone);
  }

  // ═══════════════════════════════════════════
  //  UTILS
  // ═══════════════════════════════════════════
  private onAllianceJoined(payload: any): void {
    if (!payload || !payload.alliance) return;
    this.mmoState.setAlliance(payload.alliance);
    EventBus.emit('mmo-alliance-updated');
    this.close();
  }

  public close(): void {
    this.hideTooltip();
    if (this.searchOverlay) { this.searchOverlay.remove(); this.searchOverlay = undefined; this.searchInput = undefined; this.sortSelect = undefined; }
    if (this.paginationOverlay) { this.paginationOverlay.remove(); this.paginationOverlay = undefined; }
    if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
    EventBus.emit('chat-input-unfocused');
    if (this.maskGraphics) { this.maskGraphics.destroy(); this.maskGraphics = undefined; }
    EventBus.off(MMOEvents.ALLIANCE_LIST, this.onAllianceListHandler);
    EventBus.off(MMOEvents.ALLIANCE_JOINED, this.onAllianceJoinedHandler);
    closeDialog(this.scene, this);
  }
}
